import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "@/context/UserContext";
import { hasPendingLikes, replayPendingLikes } from "@/helpers/pending-like-storage";

/**
 * Replays guest like intents after Auth0 sign-in and user hydration.
 */
export default function useReplayPendingLikes(): void {
    const queryClient = useQueryClient();
    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const replayInFlightRef = useRef(false);
    const userId = userState?.id ? String(userState.id) : "";
    const isRegisteredUser = Boolean(isAuthenticated && userId && userState?.emailAddress);

    useEffect(() => {
        if (!isRegisteredUser || !hasPendingLikes() || replayInFlightRef.current) {
            return;
        }
        replayInFlightRef.current = true;
        void replayPendingLikes(userId)
            .then((replayedCount) => {
                if (replayedCount <= 0) {
                    return;
                }
                void queryClient.invalidateQueries({ queryKey: ["property-like-counts"], exact: false });
                void queryClient.invalidateQueries({ queryKey: ["comment-like-counts"], exact: false });
                void queryClient.invalidateQueries({ queryKey: ["puzzle-feed"], exact: false });
                void queryClient.invalidateQueries({ queryKey: ["playable-property-comments"], exact: false });
                void queryClient.invalidateQueries({
                    queryKey: ["puzzle-scoreboard-current-user"],
                    exact: false,
                });
            })
            .finally(() => {
                replayInFlightRef.current = false;
            });
    }, [isRegisteredUser, queryClient, userId]);
}
